import { Navigate, Route, Routes } from 'react-router-dom';
import { useAuth } from './lib/auth.jsx';
import { WorkspaceProvider } from './lib/workspace.jsx';
import { PanelsProvider } from './lib/panels.jsx';
import { ShortcutsProvider } from './lib/shortcuts.jsx';
import { NotificationOpener } from './lib/notification-open.jsx';
import Shell from './components/Shell.jsx';
import LoginView from './views/LoginView.jsx';
import {
  VerifyEmailView,
  ResetPasswordView,
  AcceptInviteView,
} from './views/TokenViews.jsx';
import LinkDeviceView from './views/LinkDeviceView.jsx';
import TeamView from './views/TeamView.jsx';
import ChatView from './views/ChatView.jsx';
import TicketsView from './views/TicketsView.jsx';
import DocsView from './views/DocsView.jsx';
import AnalyticsView from './views/AnalyticsView.jsx';
import IntegrationView from './views/IntegrationView.jsx';

export function App() {
  const { status } = useAuth();

  if (status === 'checking') {
    return (
      <div className="boot">
        <div className="boot__mark">Superchat</div>
        <div className="boot__hint">Restoring your session…</div>
      </div>
    );
  }

  /**
   * Reached from an emailed link, so these must resolve the same whether or not
   * there is a session — the recipient may be on another machine, or have no
   * account yet. They are declared in both branches rather than lifted out,
   * because the anonymous branch below swallows everything with a catch-all
   * redirect to /login.
   */
  const tokenRoutes = (
    <>
      <Route path="/verify-email" element={<VerifyEmailView />} />
      <Route path="/reset-password" element={<ResetPasswordView />} />
      <Route path="/accept-invite" element={<AcceptInviteView />} />
      {/* Not an emailed link, but it belongs here for the same reason: the
          desktop app opens it in whatever browser is default, which may have
          no session, and the code in the URL must survive signing in. */}
      <Route path="/link" element={<LinkDeviceView />} />
    </>
  );

  if (status === 'anonymous') {
    return (
      <Routes>
        <Route path="/login" element={<LoginView />} />
        {tokenRoutes}
        <Route path="*" element={<Navigate to="/login" replace />} />
      </Routes>
    );
  }

  return (
    <WorkspaceProvider>
      <PanelsProvider>
        {/* Shortcuts belong to the agent, not the tenant, so the provider sits
            above the routes and survives an app switch. */}
        <ShortcutsProvider>
          {/* Above the routes: the tab a notification focuses may be sitting on
              any view, so the listener must outlive whichever one that is. */}
          <NotificationOpener />
          <Routes>
            {tokenRoutes}
            <Route element={<Shell />}>
              <Route path="/chat" element={<ChatView />} />
              <Route path="/tickets" element={<TicketsView />} />
              <Route path="/docs" element={<DocsView />} />
              <Route path="/analytics" element={<AnalyticsView />} />
              <Route path="/integration" element={<IntegrationView />} />
              <Route path="/team" element={<TeamView />} />
            </Route>
            <Route path="*" element={<Navigate to="/chat" replace />} />
          </Routes>
        </ShortcutsProvider>
      </PanelsProvider>
    </WorkspaceProvider>
  );
}

export default App;
